import { deriveSplitOperationId, deriveOperationIdFromKey } from './optiPlaceAnalysisInput';

/**
 * 8G.2D.2D.4 §6 — THE SKILL-SPLIT SURFACE'S OPERATION IDENTITY.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A MODULE AND NOT AN EXPRESSION INSIDE THE DRAWER
 * ═══════════════════════════════════════════════════════════════════════════
 * The identity used to be built inline in `SuggestionsDrawer.executeSplit`,
 * which meant the only thing a test could do about it was grep the component
 * for a variable name. This programme has now had three pins pass against code
 * that did not do what the pin claimed, so the construction lives here where a
 * test can call it and a controlled breakage can actually fail.
 *
 * ── THE MEASURED ALIAS THIS EXISTS TO PREVENT ──────────────────────────────
 * The identity was source + job ids + tech ids. The REQUEST additionally
 * carries `scheduledStartTime`, and `split-ticket` computes sequence 1's start
 * as `scheduledStartTime || originalTicket.start_time`, writing the result to
 * `p_parts[0].start_time`.
 *
 * So two requests differing ONLY in the schedule produced ONE operation id
 * while asking for materially different structural acts. The authority does
 * not compare `p_parts`: for a same-source repeat of a known id it answers
 * ALREADY_APPLIED and returns the FIRST family. The operator's second,
 * different decision is reported as the first one, already done.
 *
 * That is an input-canonicalization collision. Moving to SHA-256 in 8G.2D.2D.3
 * did nothing for it, because the differing field was never in the hashed
 * bytes at all.
 */

export interface OptimizerSplitAction {
  ticket_id: string;
  from_json?: { startTime?: string | null } | null;
  metadata?: {
    isWaiterOrScheduled?: boolean;
    splitGroups?: Array<{ jobIds: string[]; suggestedTechId?: string | null }>;
  } | null;
}

/**
 * The schedule this request will actually send — and therefore the one the
 * authority will actually act on.
 *
 * `from_json.startTime` is a SNAPSHOT of the source's start time taken when
 * the optimizer produced the suggestion. It can differ between two runs for
 * the same source with identical grouping, because many paths move a ticket's
 * start in between (`EditTicketDetailsModal`, `useAutoBump`,
 * `DueOutMitigationModal`, `EODRolloverModal`, `pullForwardPlanner`). That is
 * what makes this a field that can vary independently of the grouping, rather
 * than a value derivable from it.
 */
export function effectiveScheduledStartTime(
  action: OptimizerSplitAction,
): string | null {
  return action.metadata?.isWaiterOrScheduled
    ? action.from_json?.startTime ?? null
    : null;
}

/**
 * The canonical proposal this surface is asking for.
 *
 * Sequence 1 is the ONLY part whose start the request determines. Parts 2+
 * chain from the previous part's OptiFinish inside the Edge function, derived
 * from technician efficiency, breaks and shift end — server state that is not
 * part of the human decision. Including it would make the id change between a
 * request and its own retry, destroying the durability the id exists for.
 */
export function buildSuggestionsSplitParts(action: OptimizerSplitAction): Array<{
  job_ids: string[];
  tech_id: string | null;
  start_time: string | null;
}> {
  const scheduled = effectiveScheduledStartTime(action);
  return (action.metadata?.splitGroups ?? []).map((g, i) => ({
    job_ids: [...g.jobIds].sort(),
    tech_id: g.suggestedTechId ?? null,
    start_time: i === 0 ? scheduled : null,
  }));
}

/** The durable identity for one confirmed skill-split proposal. */
export function deriveSuggestionsSplitOperationId(
  action: OptimizerSplitAction,
): Promise<string> {
  return deriveSplitOperationId(action.ticket_id, buildSuggestionsSplitParts(action));
}

/**
 * 8G.2D.3.1 §5 — THE MERGE IDENTITY MUST SURVIVE ITS OWN SUCCESS.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT WAS WRONG WITH THE FIRST VERSION
 * ═══════════════════════════════════════════════════════════════════════════
 * 8G.2D.3 keyed this on `split_group_id` + the sorted LIVE sibling ids. That is
 * family-canonical and click-independent, and it is still wrong: **a successful
 * merge supersedes every live sibling**, so the second input no longer exists
 * the moment the operation it identifies has committed. An identity built from
 * a fact that its own success destroys cannot recover a lost response.
 *
 * ── WHAT THE DATABASE ACTUALLY KEEPS, MEASURED ─────────────────────────────
 * After a committed merge:
 *   - the superseded children KEEP `split_group_id` and `split_parent_id`;
 *   - the merged target carries NO split lineage, so it can never be read as
 *     a sibling of the family it replaced;
 *   - ZERO live siblings remain.
 *
 * That last fact is the proof that the group id ALONE is sufficient. A merge
 * needs two live siblings; a committed merge leaves none; therefore a family
 * can have **at most one successful merge act**, and one identity per family
 * cannot collide with a second act that cannot exist. `split_group_id` is
 * itself the split's own operation id, so it is unique per family.
 *
 * Historical child membership was considered and rejected: it is derivable
 * from the group, so it adds no information, and it reintroduces exactly the
 * failure it would be meant to fix — if the child set differs between two
 * derivations, the id moves and recovery breaks again.
 */
export function buildMergeProposalKey(splitGroupId: string): string {
  return JSON.stringify({ act: 'structural_merge', split_group_id: splitGroupId });
}

/**
 * The durable identity for one confirmed merge of one split family.
 *
 * Reproducible before the commit, after the commit, after a refresh, and from
 * any sibling in the family — because the only input is a fact the merge does
 * not touch.
 */
export function deriveMergeOperationId(splitGroupId: string): Promise<string> {
  return deriveOperationIdFromKey(buildMergeProposalKey(splitGroupId));
}
